import React, { useMemo, useState } from 'react';
import { browserTz, fmtSlot, isPast, SCHED_STATUS } from '../../lib/schedulingApi';
import { Empty, Loading, Pill } from '../aiinterview/recruiter/common';
import InterviewDetailDialog from './InterviewDetailDialog';
import SyncBadge from './SyncBadge';
import { VERDICT_TONE } from '../../lib/roomApi';

const FILTERS = [
  ['upcoming', 'Upcoming', (b) => b.status === 'scheduled' && !isPast(b.end)],
  ['awaiting', 'Awaiting candidate', (b) => b.status === 'awaiting_candidate'],
  ['outcome', 'Needs outcome', (b) => b.status === 'scheduled' && isPast(b.end)],
  ['done', 'Completed', (b) => ['completed', 'no_show'].includes(b.status)],
  ['closed', 'Cancelled / expired', (b) => ['cancelled', 'expired'].includes(b.status)],
  ['all', 'All', () => true],
];

export default function InterviewsTab({ rows, reload }) {
  const [filter, setFilter] = useState('upcoming');
  const [mine, setMine] = useState(false);
  const [open, setOpen] = useState(null);
  const tz = browserTz();
  const base = useMemo(() => (rows || []).filter((b) => !mine || b.is_mine), [rows, mine]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map(([k, , fn]) => [k, base.filter(fn).length])), [base]);
  const shown = useMemo(() => {
    const list = base.filter(FILTERS.find(([k]) => k === filter)[2]);
    return filter === 'upcoming' ? [...list].sort((a, b) => (a.start || '').localeCompare(b.start || '')) : list;
  }, [base, filter]);
  if (!rows) return <Loading />;
  return (
    <div data-testid="scheduled-interviews-tab">
      <div className="iv-row" style={{ justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div className="iv-chips" style={{ marginBottom: 0 }}>
          {FILTERS.map(([k, l]) => (
            <button type="button" key={k} className={`iv-chip ${filter === k ? 'is-on' : ''}`} onClick={() => setFilter(k)} data-testid={`sched-filter-${k}`}>{l} <span style={{ opacity: 0.6 }}>{counts[k]}</span></button>
          ))}
        </div>
        <label className="iv-row iv-small" style={{ gap: '0.4rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={mine} onChange={() => setMine((m) => !m)} data-testid="sched-mine-toggle" /> Only interviews I'm on
        </label>
      </div>
      {!shown.length ? <Empty testId="sched-empty" title="Nothing here yet" body="Schedule a human interview from a pool, or send a candidate a link to pick a time." /> : (
        <div className="iv-scroll-x">
          <table className="iv-table" data-testid="sched-table">
            <thead><tr><th>Candidate</th><th>Interview</th><th>When ({tz})</th><th>Interviewers</th><th>Status</th></tr></thead>
            <tbody>
              {shown.map((b) => {
                const m = SCHED_STATUS[b.status] || { label: b.status, tone: 'slate' };
                return (
                  <tr key={b.id} onClick={() => setOpen(b)} data-testid={`sched-row-${b.id}`}>
                    <td><div style={{ fontWeight: 700, color: '#002A38' }}>{b.candidate_name}</div><div className="iv-small">{b.job_title}</div></td>
                    <td>{b.title}<div className="iv-small">{b.duration_min} min · {b.pool_name}</div></td>
                    <td className="iv-small">{b.start ? fmtSlot(b.start, tz) : '—'}</td>
                    <td className="iv-small">{b.interviewers.map((i) => i.name).join(', ') || '—'}{b.is_mine && <Pill tone="sky">You</Pill>}</td>
                    <td><Pill tone={m.tone}>{m.label}</Pill>{b.status === 'scheduled' && b.calendar_sync && <div style={{ marginTop: 4 }}><SyncBadge b={b} testId={`sched-sync-${b.id}`} /></div>}{b.panel?.verdict ? <div style={{ marginTop: 4 }}><Pill tone={VERDICT_TONE[b.panel.verdict] || 'slate'} testId={`sched-verdict-${b.id}`}>{b.panel.verdict}</Pill></div> : b.panel?.total && b.panel.submitted ? <div className="iv-small">{b.panel.submitted}/{b.panel.total} scorecards</div> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <InterviewDetailDialog booking={open} onClose={() => setOpen(null)} onChanged={reload} />
    </div>
  );
}
